/*
Purpose: List and paginate evidence items for the current user/context.
Audience: Users browsing their evidence to open details.
What it does:
- Renders a grid/list of evidence with basic metadata
- Opens Results Modal to view and analyze an item
Interplay:
- Works with Results Modal for detailed interactions
*/
import React, {useState, useEffect, useMemo} from 'react';
import {db} from '../../firebase';
import {collection, query, where, onSnapshot, orderBy, doc, deleteDoc} from 'firebase/firestore';
import {getStorage, ref, deleteObject} from "firebase/storage";
import UniversalEvidenceWindow from './UniversalEvidenceWindow';
import { getEvidenceActions } from './evidenceActionConfigs';
import { EvidenceResultsModal } from '../modals/results/wrappers/CleanResultsModals';
import SlideToDelete from '../buttons/SlideToDelete';
import '../../styles/EvidenceList.css';


const EvidenceList = ({casefileId, userId, currentUser, selectedEvidence, onSelectionChange, casefileName}) => {
  const [evidence, setEvidence] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [evidenceToDelete, setEvidenceToDelete] = useState(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [resultsEvidence, setResultsEvidence] = useState(null);

  useEffect(() => {
    if (!casefileId) {
        setIsLoading(false);
        return;
    };
    
    const q = query(collection(db, 'casefiles', casefileId, 'evidence'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const evidenceDocs = querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
        setEvidence(evidenceDocs);
        setIsLoading(false);
    });
    return () => unsubscribe();
  }, [casefileId]);

  // Memoize evidence IDs to prevent unnecessary re-renders and re-subscriptions
  const evidenceIdsString = useMemo(() => 
    evidence.map(item => item.id).join(','), 
    [evidence]
  );

  // Fetch analysis counts for each evidence item (batched to respect Firestore limits)
  useEffect(() => {
    if (evidence.length === 0) return;

    const evidenceIds = evidence.map(item => item.id);
    if (evidenceIds.length === 0) return;

    // Helper to split into chunks of size N
    const chunk = (arr, size) => {
      const chunks = [];
      for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
      return chunks;
    };

    // Firestore array-contains-any supports up to 10 values
    const idChunks = chunk(evidenceIds, 10);

    const unsubs = [];
    const analysisCountsByEvidence = {};
    evidenceIds.forEach(id => { analysisCountsByEvidence[id] = 0; });

    idChunks.forEach((ids) => {
      const qRef = query(
        collection(db, 'orders'),
        where('evidenceIds', 'array-contains-any', ids)
      );
      const unsub = onSnapshot(qRef, (snap) => {
        // Reset counts for this chunk before recounting
        ids.forEach(id => { analysisCountsByEvidence[id] = 0; });
        snap.forEach((docSnap) => {
          const order = docSnap.data();
          if (order.evidenceIds && Array.isArray(order.evidenceIds)) {
            order.evidenceIds.forEach((evidenceId) => {
              if (analysisCountsByEvidence.hasOwnProperty(evidenceId)) {
                analysisCountsByEvidence[evidenceId]++;
              }
            });
          }
        });
        // Push aggregated counts into state
        setEvidence(prev => prev.map(item => ({
          ...item,
          analysisCount: analysisCountsByEvidence[item.id] || 0
        })));
      });
      unsubs.push(unsub);
    });

    return () => unsubs.forEach(u => u());
  }, [evidenceIdsString]); // Use memoized string to prevent unnecessary effect runs



  // Results modal updates handled by real-time subscriptions

  const handleDeleteConfirm = async () => {
    if (!evidenceToDelete) return;
    try {
        await deleteDoc(doc(db, 'casefiles', casefileId, 'evidence', evidenceToDelete.id));
        const storage = getStorage();
        const fileRef = ref(storage, evidenceToDelete.filePath);
        await deleteObject(fileRef);
    } catch (error) {
        console.error("Error deleting evidence:", error);
    } finally {
        setConfirmModalOpen(false);
        setEvidenceToDelete(null);
    }
  };

  const openResultsModal = (evidenceItem) => {
    if (!evidenceItem || !evidenceItem.id) {
      console.error('Cannot open results modal: evidenceItem is missing or has no id', evidenceItem);
      return;
    }
    setResultsEvidence(evidenceItem);
    setIsResultsModalOpen(true);
  };

  const handleShare = (evidenceItem) => {
    // Share functionality - copy link to clipboard
    if (navigator.share) {
      navigator.share({
        title: evidenceItem.fileName,
        text: `Check out this evidence: ${evidenceItem.fileName}`,
        url: window.location.href
      }).catch(err => console.log('Error sharing:', err));
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('Link copied to clipboard!'))
        .catch(err => console.error('Failed to copy:', err));
    }
  };
  

  // Small helper for human-readable file size
  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return null;
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    const idx = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = Math.round((bytes / Math.pow(1024, idx)) * 100) / 100;
    return `${value} ${units[idx]}`;
  };

  if (isLoading) return <p>Loading evidence...</p>;

  return (
    <div className="evidence-list-container">
      {evidence.length === 0 ? <p>No evidence has been submitted.</p> : (
        <div className="evidence-tiles-container">
          {evidence.map((item) => {
            const isSelected = selectedEvidence.some(selectedItem => selectedItem.id === item.id);
            
            // Generate action buttons for casefile context
            const actionButtons = getEvidenceActions('casefile', {
              onOpenResults: () => openResultsModal(item),
              onShare: () => handleShare(item)
            });
            
            return (
                 <UniversalEvidenceWindow
                   key={item.id}
                   evidence={item}
                   mode="tile"
                   actionButtons={actionButtons}
                   onOpenModal={openResultsModal}
                   currentUser={currentUser}
                   formatFileSize={formatFileSize}
                   isSelected={isSelected}
                   onSelectionChange={onSelectionChange}
                   casefileName={casefileName}
                   casefileId={casefileId}
                   // Summary props (not used in tile mode but required)
                   evidenceSummary={item.summary || ''}
                   onSummaryChange={null}
                   isEditingSummary={false}
                   onEditingSummaryChange={null}
                   isSavingSummary={false}
                   onSavingSummaryChange={null}
                 />
            );
          })}
        </div>
      )}

      <EvidenceResultsModal
        isOpen={isResultsModalOpen}
        onClose={() => {
          setIsResultsModalOpen(false);
          setResultsEvidence(null); // Clear evidence state when closing
        }}
        evidenceId={resultsEvidence?.id}
        evidenceName={resultsEvidence?.fileName}
        casefileId={casefileId}
        casefileName={casefileName}
        currentUser={currentUser}
        slideMode={true}
      />

      {isConfirmModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-content confirmation-modal">
            <h3>Confirm Deletion of Evidence</h3>
            <p>This action is permanent and cannot be undone.</p>
            <SlideToDelete
              onConfirm={handleDeleteConfirm}
              onCancel={() => {
                setConfirmModalOpen(false);
                setEvidenceToDelete(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EvidenceList;